import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';

interface LifecycleData {
    readonly avgDurationDays: number;
    readonly redemptionRate: number;
    readonly extensionRate: number;
    readonly inventoryRate: number;
    readonly avgExtensionsPerContract: number;
}

interface ContractLifecycleChartProps {
    readonly data: LifecycleData | null;
    readonly loading?: boolean;
}

function ContractLifecycleChart({
    data,
    loading = false,
}: ContractLifecycleChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || !data) {
        return <ChartPlaceholder loading={loading} hasData={!!data} />;
    }

    // Pie chart for contract outcomes
    const outcomeData = [
        {
            name: t('statistics.bi.redeemed', 'Redeemed'),
            y: data.redemptionRate,
            color: '#52c41a',
        },
        {
            name: t('statistics.bi.movedToInventory', 'Moved to Inventory'),
            y: data.inventoryRate,
            color: '#faad14',
        },
        {
            name: t('statistics.bi.stillActive', 'Still Active/Other'),
            y: Math.max(0, 100 - data.redemptionRate - data.inventoryRate),
            color: '#1890ff',
        },
    ].filter(d => d.y > 0);

    const options: Highcharts.Options = {
        chart: {
            type: 'pie',
            height: 350,
        },
        title: {
            text: undefined,
        },
        subtitle: {
            text:
                t('statistics.bi.avgDuration', 'Avg. Duration') +
                ': ' +
                Math.round(data.avgDurationDays) +
                ' ' +
                t('statistics.bi.days', 'days') +
                ' | ' +
                t('statistics.bi.avgExtensions', 'Avg. Extensions') +
                ': ' +
                data.avgExtensionsPerContract.toFixed(1),
        },
        tooltip: {
            pointFormat: '<b>{point.percentage:.1f}%</b>',
        },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                },
                innerSize: '50%', // Donut style
                showInLegend: true,
            },
        },
        series: [
            {
                type: 'pie',
                name: t('statistics.bi.contractOutcome', 'Contract Outcome'),
                data: outcomeData,
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default ContractLifecycleChart;
